<template>
  <div class="compact-item" :class="itemClassRef">
    <button v-if="selectModeRef" type="button" class="overlay" @click="emit('click')"></button>
    <router-link v-else-if="linkModeRef" :to="pageUrl!" class="overlay"/>
    <div class="item-content">
      <div class="item-icon">
        <slot name="icon"></slot>
      </div>
      <div class="item-body">
        <div class="item-title-row">
          <div class="item-title no-overflow">
            <slot name="title"></slot>
          </div>
          <div v-if="$slots.statusText" class="item-status badge" :class="props.statusStyle">
            <slot name="statusText"></slot>
          </div>
        </div>
        <div class="item-subtitle no-overflow">
          <slot name="subtitle"></slot>
        </div>
      </div>
      <div v-if="showHighlightSourceRef">
        <label class="btn-highlight-label">
          <input type="checkbox" class="btn-highlight" :checked="highlightSourceSelected" @change="onHighlightChange"/>
        </label>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.compact-item {
  position: relative;
  min-height: 45px;
  padding: 0.25rem 0;
  display: flex;

  &.item-small {
    min-height: 40px;
    padding: 0;
  }

  &.item-small .item-title-row {
    font-size: 0.875rem;

    @media (min-width: 768px) {
      .item-title {
        font-size: 0.875rem;
        line-height: 1.2;
      }
    }    
  }

  &.item-shadow {
    border-top: 1px solid #f8f8f8;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);

    &:not(.static):hover {
      box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.15);
    }
  }

  &.raised,
  &.show-hover:hover {
    box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.15); // stronger shadow
  }

  .item-content {
    width: 100%;
    padding: 0 0.25rem;
    display: grid;
    column-gap: 0.5rem;
    grid-template-columns: max-content 1fr;
    align-items: center;
  }

  &.highlight-source .item-content {
    grid-template-columns: max-content 1fr max-content;
    padding-right: 0.5rem;
  }

  .item-icon {
    padding: 2px;

    img {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }
  }

  .item-body {
    display: flex;
    flex-direction: column;    
    min-width: 0; // allow content to shrink (will trigger ellipsis)
  }

  .item-title-row {
    display: flex;
    align-items: start;
    gap: 0.5rem;
  }

  .item-title {
    font-size: 0.875rem;
  }

  .item-status {
    margin-left: auto;
    padding: 0 0.25rem 0;
    background-color: #ddd;
    color: #333;

    &.success {
      background-color: var(--bs-success);
    }
    &.warning {
      background-color: var(--bs-warning);
    }
    &.info {
      background-color: var(--bs-info);
      color: white;
    }
  }

  .item-subtitle {
    font-size: 0.75rem;
    color: #888;
  }

  @media (min-width: 768px) {
    .item-title {
      font-size: 1rem;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'

const props = defineProps<{
  pageUrl?: string
  static?: boolean
  selectMode?: boolean
  statusText?: string
  statusStyle?: string
  small?: boolean
  shadow?: boolean
  raised?: boolean
  noHover?: boolean
  showHover?: boolean
  highlightMode?: boolean
  highlighted?: boolean
  highlightSource?: boolean
  highlightSourceSelected?: boolean
}>()

const emit = defineEmits<{
  (e: 'click'): void,
  (e: 'highlight', value: boolean): void,
}>()

const selectModeRef = computed(() => !props.static && props.selectMode)
const linkModeRef = computed(() => !props.static && !props.selectMode && props.pageUrl)
const isInteractiveRef = computed(() => selectModeRef.value || linkModeRef.value)
const showHighlightSourceRef = computed(() => props.highlightMode && props.highlightSource)

const itemClassRef = computed(() => {
  return {
    ['item-small']: props.small,
    ['item-shadow']: props.shadow,
    static: props.static,
    raised: props.raised,
    ['show-hover']: (isInteractiveRef.value && !props.noHover) || props.showHover,
    ['highlight-source']: showHighlightSourceRef.value,
  }
})

function onHighlightChange(e: Event) {
  const ch = e.target as HTMLInputElement
  emit('highlight', ch.checked)
}

</script>
